/**
 * Boot-path Sentry helpers wired by `instrumentation-client.ts`.
 *
 * Deliberately has NO value import of `@sentry/nextjs`: lib/ specs share one
 * process and several replace `@sentry/nextjs` with a partial mock, so the
 * Sentry functions are injected instead (and unit-tested with fakes).
 */
import type * as SentryNamespace from "@sentry/nextjs";

/**
 * Requests that must NOT get a Sentry `http.client` span. Telemetry posts
 * (the OTel exporters' `/diag/ingest` and `/diag/logs` on the BFF origin,
 * Mixpanel's tracking XHRs) fire during boot and keep the idle pageload span
 * open, so the pageload duration measured telemetry traffic rather than the
 * app. Every other BFF call and RSC fetches still get spans. Covers fetch and
 * XHR — Sentry uses one predicate for both. Same exporter pattern as
 * `lib/otel/ignoreUrls.ts`.
 */
const NO_SPAN_REQUEST_PATTERN = /\/diag\/(?:ingest|logs)(?:$|\?)|mixpanel\.com/;

export const shouldCreateSpanForRequest = (url: string): boolean =>
  !NO_SPAN_REQUEST_PATTERN.test(url);

/** `performance.mark` name set by `splashScreen/removeLoader.js`. */
export const APP_READY_MARK = "app-ready";

// String-only console lines: Mixpanel replay renders objects as [object Object].
const describeError = (error: unknown): string =>
  error instanceof Error ? `${error.name}: ${error.message}` : String(error);

type SentryForAppReady = Pick<
  typeof SentryNamespace,
  "getActiveSpan" | "getRootSpan" | "spanToJSON" | "setMeasurement"
>;

/**
 * Record `app.ready` (ms since navigation start) on the pageload transaction
 * when the boot splash first lifts.
 *
 * removeLoader.js only drops a `performance.mark` — it stays import-free
 * because it runs during render from ~10 call sites. This observer turns that
 * mark into a Sentry measurement, but only while the active root span is the
 * `pageload` (a mark after a client navigation is not a boot metric). The
 * pathname is attached because removeLoader also fires on public routes
 * (welcome, register, logged-out), whose boot differs from the dashboard's.
 *
 * Sentry's browser tracing also records `mark` entries as spans on the
 * pageload, which is the fallback if the measurement is missed.
 *
 * Returns a disposer that disconnects the observer (a no-op when none was
 * registered). Production ignores it — the observer disconnects itself once
 * it has seen the mark — but tests call it so an observer that never saw the
 * mark can't outlive its test and fire on a later file's mark.
 */
export function installAppReadyMeasurement(
  sentry: SentryForAppReady,
  getPathname: () => string
): () => void {
  const noop = () => {};
  if (
    typeof PerformanceObserver === "undefined" ||
    !PerformanceObserver.supportedEntryTypes?.includes("mark")
  ) {
    return noop;
  }

  try {
    const observer = new PerformanceObserver((list) => {
      const entry = list
        .getEntriesByName(APP_READY_MARK)
        .find((e) => e.entryType === "mark");
      if (!entry) return;
      observer.disconnect();
      try {
        const activeSpan = sentry.getActiveSpan();
        const rootSpan = activeSpan && sentry.getRootSpan(activeSpan);
        if (!rootSpan || sentry.spanToJSON(rootSpan).op !== "pageload") return;
        sentry.setMeasurement(
          "app.ready",
          entry.startTime,
          "millisecond",
          rootSpan
        );
        rootSpan.setAttribute("app.ready.pathname", getPathname());
      } catch (error) {
        console.error(
          `[boot] app.ready measurement failed: ${describeError(error)}`
        );
      }
    });
    observer.observe({ type: "mark", buffered: true });
    return () => observer.disconnect();
  } catch (error) {
    console.error(
      `[boot] app.ready observer failed to register: ${describeError(error)}`
    );
    return noop;
  }
}
